import React from 'react';

export type Status = 'pago' | 'pendente' | 'vencido' | 'cancelado';

interface StatusBadgeProps {
  status: Status;
  size?: 'sm' | 'md';
  /** Modo somente o ponto colorido (sem texto) — útil em tabelas densas. */
  dotOnly?: boolean;
}

const STATUS_CONFIG: Record<Status, { label: string; dotClass: string; textClass: string; bgClass: string; borderClass: string }> = {
  pago: {
    label: 'Pago',
    dotClass: 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.6)]',
    textClass: 'text-emerald-400',
    bgClass: 'bg-emerald-500/10',
    borderClass: 'border-emerald-500/30',
  },
  pendente: {
    label: 'Pendente',
    dotClass: 'bg-primary shadow-[0_0_8px_rgba(255,170,34,0.8)]',
    textClass: 'text-primary',
    bgClass: 'bg-primary/10',
    borderClass: 'border-primary/30',
  },
  vencido: {
    label: 'Vencido',
    dotClass: 'bg-rose-500 shadow-[0_0_8px_rgba(244,63,94,0.6)]',
    textClass: 'text-rose-400',
    bgClass: 'bg-rose-500/10',
    borderClass: 'border-rose-500/30',
  },
  cancelado: {
    label: 'Cancelado',
    dotClass: 'bg-slate-500',
    textClass: 'text-slate-500',
    bgClass: 'bg-slate-700/30',
    borderClass: 'border-slate-600/30',
  },
};

/**
 * Badge unificado de status — usado em Recebimentos, ContasAPagar e Dashboard
 * para garantir consistência visual de cores e ícones.
 */
export const StatusBadge: React.FC<StatusBadgeProps> = ({ status, size = 'md', dotOnly = false }) => {
  const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.pendente;

  if (dotOnly) {
    return <div className={`w-1.5 h-1.5 rounded-full ${cfg.dotClass}`} aria-label={cfg.label} />;
  }

  const sizeClass = size === 'sm'
    ? 'text-[9px] px-2 py-0.5'
    : 'text-[10px] px-3 py-1.5';

  return (
    <span className={`inline-flex items-center gap-2 rounded-full font-bold uppercase tracking-wider transition-colors ${sizeClass} ${cfg.bgClass} ${cfg.textClass} border ${cfg.borderClass}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dotClass}`} />
      {cfg.label}
    </span>
  );
};
